import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, } from "@heroicons/react/24/outline";
import cx from "classnames";
import { useMemo } from "react";
import cn from "variant-classnames";
import { toolkitCx, toolkitTestIds } from "../../../constants";
import Checkbox from "../../atoms/Checkbox";
import Text from "../../atoms/Text/Text";
const BASE_ICON_SIZE = 24;
const getIconSize = (size) => {
    switch (size) {
        case "2xs":
            return 16;
        case "xs":
            return 20;
        case "sm":
            return BASE_ICON_SIZE;
        case "md":
            return 28;
        case "lg":
            return 32;
        case "xl":
            return 36;
        case "2xl":
            return 40;
        default:
            return 24;
    }
};
const getIcon = (type, size) => {
    const iconSize = getIconSize(size);
    switch (type) {
        case "success":
            return _jsx(CheckCircleIcon, { height: iconSize, width: iconSize });
        case "error":
        case "warning":
            return _jsx(ExclamationTriangleIcon, { height: iconSize, width: iconSize });
        case "info":
        default:
            return _jsx(InformationCircleIcon, { height: iconSize, width: iconSize });
    }
};
const getTitleSize = (size) => {
    switch (size) {
        case "2xs":
            return "sm";
        case "xs":
            return "base";
        case "sm":
            return "lg";
        case "md":
            return "xl";
        case "lg":
            return "2xl";
        case "xl":
            return "3xl";
        case "2xl":
            return "4xl";
        default:
            return "lg";
    }
};
const variants = (border, isConsentChecked, showErrorFlash) => ({
    $all: cx("rounded-lg", toolkitCx.layout.spacing.both.md, {
        "bg-gradient-to-br from-success/30 via-success/20 to-success/10": isConsentChecked,
        "bg-gradient-to-br from-error/30 via-error/20 to-error/10": showErrorFlash,
        "to-transparent": !isConsentChecked && !showErrorFlash,
    }),
    type: {
        info: cx({
            "border border-primary": border,
            "bg-gradient-to-br from-primary/20 via-primary/10": !isConsentChecked && !showErrorFlash,
        }),
        error: cx({
            "border border-error": border,
            "bg-gradient-to-br from-error/20 via-error/10": !isConsentChecked && !showErrorFlash,
        }),
        success: cx({
            "border border-success": border,
            "bg-gradient-to-br from-success/20 via-success/10": !isConsentChecked && !showErrorFlash,
        }),
        warning: cx({
            "border border-warning-300": border,
            "bg-gradient-to-br from-warning-300/20 via-warning-300/10": !isConsentChecked && !showErrorFlash,
        }),
    },
});
/**
 * The Notice component provides a customizable message box for displaying
 * information, warnings, errors, or success messages. It supports optional
 * consent checkboxes and customizable icons.
 *
 * @component
 * @param {TNotice} props - The props for the Notice component.
 * @param {string} [props.type="info"] - The type of the notice. Available options are "info", "warning", "error", and "success".
 * @param {JSX.Element|null} [props.icon] - An optional custom icon for the notice. If not provided, a default icon based on the notice type is used.
 * @param {number} [props.iconSize=24] - **DEPRECATED** - Not used anymore. Icon size is calculated based on the `size` prop.
 * @param {string} [props.size="sm"] - The size of the notice text. Inherits the sizing options from the Text component.
 * @param {React.ReactNode} [props.children] - The content of the notice.
 * @param {boolean} [props.border] - Whether to show a border around the notice.
 * @param {string} [props.className] - An optional CSS class name to apply to the notice.
 * @param {boolean} [props.withConsent] - Whether the notice should include a consent checkbox.
 * @param {boolean} [props.isConsentChecked] - The checked state of the consent checkbox. Required when `withConsent` is true.
 * @param {Function} [props.onConsentChange] - A callback function called when the consent checkbox state changes. Required when `withConsent` is true.
 * @param {boolean} [props.showErrorFlash] - Whether to show an error flash for the consent checkbox. Required when `withConsent` is true.
 * @param {string} [props.title] - The title of the notice.
 * @returns {JSX.Element} A rendered Notice component.
 */
const Notice = ({ border, type = "info", icon, size = "sm", children, className, 
// Consent props
withConsent, isConsentChecked, onConsentChange, showErrorFlash, title, }) => {
    const noticeClasses = cn(variants(border, isConsentChecked, showErrorFlash), {
        type,
        className,
    });
    const memoizedType = useMemo(() => {
        if (withConsent && isConsentChecked) {
            return "success";
        }
        if (showErrorFlash) {
            return "error";
        }
        return type;
    }, [isConsentChecked, showErrorFlash, withConsent]);
    return (_jsx("div", { className: noticeClasses, "data-testid": toolkitTestIds.notice[memoizedType], children: _jsxs("div", { className: "grid gap-2", children: [_jsxs(Text, { className: "flex items-center gap-2 !transition-none", size: getTitleSize(size), tag: "div", variant: memoizedType, children: [icon || getIcon(memoizedType, size), title && title] }), _jsxs(Text, { className: "flex items-center justify-between gap-2 !transition-none", size: size, tag: "div", variant: memoizedType, children: [children, withConsent && (_jsx("div", { className: "flex-shrink-0", children: _jsx(Checkbox, { checked: isConsentChecked, onCheckedChange: onConsentChange }) }))] })] }) }));
};
export default Notice;
//# sourceMappingURL=Notice.js.map